import { FormModal, SubmitProps } from '@/components/form-modal';
import { useToast } from '@/components/ui/hooks/use-toast';
import { bulkAssignReturningStudents } from '@/lib/api-calls';
import { airServerErrorFrom } from '@/lib/error-handling';
import { StudentOfSchool } from '@airreading/common/data';
import { useMutation } from '@tanstack/react-query';
import { ReactNode } from 'react';
import { z } from 'zod';

interface BulkAssignReturningStudentsProps {
  students: StudentOfSchool[];
  trigger: ReactNode;
  onSuccess?: () => void;
}

const formSchema = z.object({});

export const BulkAssignReturningStudents = ({ students, trigger, onSuccess }: BulkAssignReturningStudentsProps) => {
  const { toast } = useToast();

  const { mutateAsync } = useMutation({
    mutationFn: bulkAssignReturningStudents,
  });

  const onSubmit = ({ closeModal }: SubmitProps<z.infer<typeof formSchema>>) => {
    return mutateAsync({
      studentIds: students.map(({ id }) => id),
    })
      .then(({ updatedCount, skippedCount }) => {
        toast({
          title: `Assigned returning students for ${updatedCount} student(s)`,
          description:
            skippedCount > 0
              ? `${skippedCount} student(s) skipped (no end lesson or could not auto-assign).`
              : undefined,
        });
        onSuccess?.();
        closeModal();
      })
      .catch((e) => {
        toast({
          title: 'Error assigning returning students',
          description: airServerErrorFrom(e)?.message,
          variant: 'destructive',
        });
      });
  };

  return (
    <FormModal
      trigger={trigger}
      modalTitle={`Bulk assign starting lesson for returning students`}
      formSchema={formSchema}
      onSubmit={onSubmit}
      defaultValues={{}}
    >
      {() => {
        return (
          <div className="flex flex-col gap-2 pt-3">
            <p>
              Starting lesson and learning path for selected students will be auto-assigned based on their end lesson.
              Students with no end lesson will be skipped.
            </p>
          </div>
        );
      }}
    </FormModal>
  );
};
